import Container from 'components/helpers/Container';
import classnames from 'classnames';
import {
  TcDirectoryDiseaseStageProps,
  TcDirectoryDiseaseStatsProps,
  TcDirectoryListingProps,
} from '../_types';
import HeadingTag from 'components/helpers/HeadingTag';
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import { Field, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { logError, makeLoggableError } from 'lib/util/errors';
import fnNoOp from 'lib/util/no-op';
import { TcDirectoryListingClasses } from 'components/authorable/NmdpSite/TcDirectoryListing/_classes';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';

type DiseaseDetails = {
  diseaseCategoryName: string | undefined;
  specificDiseaseName: string | undefined;
};
type DiseaseApiResponse = {
  Name: string;
};

// Extract the disease category name and the specific disease name from the diseases data.
//
// NOTE: The data could contain multiple instances of the target disease category (e.g.
// in its Related section, Unrelated section, and/or Autologous section), but each instance
// may or may not include the target specific disease among its children. So we can't just
// blindly use the first matching disease category data set that we find. See WT-7976.
//
const getDiseaseNames = (
  parentDiseaseId: number | undefined,
  stageId: number | undefined,
  diseases: TcDirectoryDiseaseStatsProps
): DiseaseDetails => {
  let diseaseCategoryName: string | undefined;
  let specificDiseaseName: string | undefined;

  if (!parentDiseaseId && !stageId) {
    // User did not search by Diagnosis so we'll return empty strings
    return { diseaseCategoryName, specificDiseaseName };
  }

  const categories = ['related', 'unrelated', 'autologus'];
  categories.forEach((category) => {
    const diseaseCategoryObjects = diseases[category];
    diseaseCategoryObjects.forEach((diseaseCategoryObject) => {
      if (diseaseCategoryObject.fields?.diseaseId.value === parentDiseaseId) {
        // Found a matching disease category
        if (!diseaseCategoryName) {
          diseaseCategoryName = diseaseCategoryObject.displayName;
        }
        if (!specificDiseaseName) {
          diseaseCategoryObject.fields?.stages?.forEach((stageObject) => {
            if (stageObject.fields?.diseaseId.value === stageId) {
              // Found a matching specific disease
              specificDiseaseName = stageObject.displayName;
            }
          });
        }
      }
    });
  });
  return { diseaseCategoryName, specificDiseaseName };
};

const TransplantByDiseaseTab = ({ fields }: TcDirectoryListingProps): React.JSX.Element => {
  // Parse IDs from URL parameters
  const { sitecoreContext: ctx } = useSitecoreContext();
  const urlDiseaseCategoryId = !ctx.txSearchIds?.at(0) ? undefined : ctx.txSearchIds[0];
  const urlSpecificDiseaseId = urlDiseaseCategoryId ? ctx.txSearchIds?.at(1) ?? 0 : undefined;

  // Lookup disease names for IDs
  const {
    diseaseCategoryName: incomingDiseaseCategoryName,
    specificDiseaseName: incomingSpecificDiseaseName,
  } = getDiseaseNames(
    urlDiseaseCategoryId,
    urlSpecificDiseaseId,
    fields?.diseases as TcDirectoryDiseaseStatsProps
  );
  const [specificDiseaseName, setSpecificDiseaseName] = useState<string | undefined>(
    incomingSpecificDiseaseName
  );
  const [diseaseCategoryName, setDiseaseCategoryName] = useState(
    incomingDiseaseCategoryName ? incomingDiseaseCategoryName : '{Unknown category name}'
  );

  // Display years
  const { displayYears: rawDisplayYears } = fields?.listing ?? { displayYears: [] };
  const displayYears = rawDisplayYears
    .map((year: Sitecore.Override.ItemEx) => Number.parseInt(year.name ?? '0', 10))
    .filter((x) => !Number.isNaN(x) && x > 0)
    .sort((a, b) => a - b);

  // Retrieve transplant stats
  useEffect(() => {
    if (urlSpecificDiseaseId && specificDiseaseName === undefined) {
      const apiUrl = `/api/sitecore/tcdirectory/disease?id=${urlDiseaseCategoryId}_${
        urlSpecificDiseaseId ?? 0
      }`;
      const abortController = new AbortController();
      let requestProcessed = false;
      const diseaseData = async () => {
        const response = await axios.get<Array<DiseaseApiResponse>>(apiUrl);
        requestProcessed = true;
        const apiResponse = response.data?.at(0)?.Name;
        if (specificDiseaseName === undefined && apiResponse) {
          if (urlSpecificDiseaseId === 0) {
            setDiseaseCategoryName(apiResponse);
            setSpecificDiseaseName('');
          } else {
            setSpecificDiseaseName(apiResponse);
          }
        }
      };
      diseaseData().catch((error) => {
        requestProcessed = true;
        if (axios.isCancel(error)) {
          return;
        }
        if (axios.isAxiosError(error)) {
          logError('TransplantByDiseases:LoadData', error, apiUrl);
        } else {
          logError('TransplantByDiseases:LoadData', makeLoggableError(error), apiUrl);
        }
      });
      return () => {
        if (requestProcessed) {
          return;
        }
        abortController.abort();
      };
    }
    return fnNoOp;
  }, [diseaseCategoryName, specificDiseaseName, urlSpecificDiseaseId, urlDiseaseCategoryId]);

  const processDiseaseTotals = (diseaseCategoryId: number, specificDiseaseId: number) => {
    const processStages = (stages: TcDirectoryDiseaseStageProps[]) => {
      /**
       * Read a numeric field value with a default of 0
       * @param {Field<number> | undefined} fld The field to read from
       * @returns The {@param Field.value}, or 0 if there is no field.
       */
      const getFieldValue = (fld?: Field<number>) => fld?.value ?? 0;
      // Find the disease data for the given specificDiseaseId value.
      // If it's zero, aggregate totals will be returned instead of specific disease stats.
      const diseaseStage = stages?.find(
        (s) => (s.fields?.diseaseId?.value ?? -1) === specificDiseaseId
      ) ?? { fields: undefined };
      return [
        getFieldValue(diseaseStage.fields?.age1),
        getFieldValue(diseaseStage.fields?.age2),
        getFieldValue(diseaseStage.fields?.age3),
        getFieldValue(diseaseStage.fields?.age4),
        getFieldValue(diseaseStage.fields?.total),
      ];
    };
    const categories = ['related', 'unrelated', 'autologus'];
    const results = categories.map((category) => {
      const items = fields?.diseases?.[category] || [];
      let age1 = 0;
      let age2 = 0;
      let age3 = 0;
      let age4 = 0;
      let total = 0;
      items.forEach((item) => {
        if (
          !diseaseCategoryId === null ||
          !diseaseCategoryId ||
          item?.fields?.diseaseId?.value === diseaseCategoryId
        ) {
          const [a1, a2, a3, a4, t] = processStages(item?.fields?.stages || []);
          age1 += a1;
          age2 += a2;
          age3 += a3;
          age4 += a4;
          total += t;
        }
      });
      return [age1, age2, age3, age4, total];
    });
    const overallTotal = results.reduce(
      (acc, [a1, a2, a3, a4, total]) => {
        acc[0] += a1;
        acc[1] += a2;
        acc[2] += a3;
        acc[3] += a4;
        acc[4] += total;
        return acc;
      },
      [0, 0, 0, 0, 0]
    );
    return [...results, overallTotal];
  };

  // Tables
  const buildTableHeader = (headingText: string, introText: string) => {
    return (
      <div>
        <HeadingTag headingLevel="h3" className={TcDirectoryListingClasses.Heading.Medium}>
          {headingText}
        </HeadingTag>
        <p className={TcDirectoryListingClasses.Paragraph}>{introText}</p>
      </div>
    );
  };
  const columnNames = [
    { id: '0', value: '' },
    { id: '1', value: '0-18' },
    { id: '2', value: '19-44' },
    { id: '3', value: '45-64' },
    { id: '4', value: '65+' },
    { id: 'total', value: 'Total' },
  ];
  const tdClass = classnames('p-2.5', 'border', 'border-gray-quartz');
  const trClass = classnames(
    'p-2.5',
    'even:bg-gray-whisper',
    'mb-2',
    'sm:mb-0',
    'border',
    'border-gray-quartz'
  );
  const thClass = classnames('p-2.5', 'border', 'border border-gray-quartz');
  const buildTableRows = (diseaseCategoryId: number, specificDiseaseId: number) => {
    const rowData = [
      { key: 'related', value: 'Related' },
      { key: 'unrelated', value: 'Unrelated' },
      { key: 'autologus', value: 'Autologous' },
      { key: 'total', value: 'Total' },
    ];
    const diseaseData = processDiseaseTotals(diseaseCategoryId, specificDiseaseId);
    return rowData.map((item) => {
      const data =
        item.key === 'total'
          ? diseaseData[3]
          : diseaseData[['related', 'unrelated', 'autologus'].indexOf(item.key)];
      return (
        <Tr key={item.key} className={trClass}>
          <Th className={tdClass}>{item.value}</Th>
          <Td className={tdClass}>{data[0]}</Td>
          <Td className={tdClass}>{data[1]}</Td>
          <Td className={tdClass}>{data[2]}</Td>
          <Td className={tdClass}>{data[3]}</Td>
          <Td className={tdClass}>{data[4]}</Td>
        </Tr>
      );
    });
  };
  const tableHeaders = columnNames.map((column) => {
    return (
      <Th key={column.id} className={thClass}>
        {column.value}
      </Th>
    );
  });
  const buildTable = (diseaseCategoryId: number, specificDiseaseId: number) => {
    return (
      <Table className="mb-4">
        <Thead>
          <Tr className={classnames('text-left', 'font-bold')}>{tableHeaders}</Tr>
        </Thead>
        <Tbody>{buildTableRows(diseaseCategoryId, specificDiseaseId)}</Tbody>
      </Table>
    );
  };
  const buildTableForAllDiseases = () => {
    return buildTable(0, 0);
  };
  const buildTableForDiseaseCategory = (diseaseCategoryId: number) => {
    return buildTable(diseaseCategoryId, 0);
  };
  const buildTableForSpecificDisease = (diseaseCategoryId: number, specificDiseaseId: number) => {
    return buildTable(diseaseCategoryId, specificDiseaseId);
  };

  // Display years
  const startYear: number = displayYears.length < 1 ? new Date().getFullYear() : displayYears[0];
  const endYear: number =
    displayYears.length < 2 ? startYear : displayYears[displayYears.length - 1];
  const tableIntroText = `Number of transplants by age reported from January 1, ${startYear} to December 31st, ${endYear} (includes marrow, PBSC and cord blood)`;

  const shouldDisplayAllDiseasesTable = !urlDiseaseCategoryId;
  const shouldDisplaySpecificDiseaseTable =
    (urlDiseaseCategoryId ?? 0) > 0 && (urlSpecificDiseaseId ?? 0) > 0;
  const shouldDisplayDiseaseCategoryTable: boolean = (urlDiseaseCategoryId ?? 0) > 0;

  return (
    <Container
      size="standard"
      dataComponent="authorable/nmdpsite/tcdirectory/TcDirectoryDiseaseStatsProps"
    >
      <>
        {shouldDisplayAllDiseasesTable && (
          <>
            {buildTableHeader('All Diseases', tableIntroText)}
            {buildTableForAllDiseases()}
          </>
        )}
        {shouldDisplaySpecificDiseaseTable && (
          <>
            {buildTableHeader(specificDiseaseName ?? '', tableIntroText)}
            {buildTableForSpecificDisease(urlDiseaseCategoryId ?? 0, urlSpecificDiseaseId ?? 0)}
            <br />
          </>
        )}
        {shouldDisplayDiseaseCategoryTable && (
          <>
            {buildTableHeader(diseaseCategoryName ?? '', tableIntroText)}
            {buildTableForDiseaseCategory(urlDiseaseCategoryId ?? 0)}
          </>
        )}
        <p className={TcDirectoryListingClasses.Paragraph}>
          Treatments may be similar for diseases within a group. It might be helpful to look at
          centers that have done transplants for a specific disease and centers that have done
          transplants for any <b>corresponding broad disease categories</b>.
        </p>
        <p className={TcDirectoryListingClasses.Paragraph}>
          Centers are not required to report autologous transplants so the numbers might be
          incomplete.
        </p>
        <p className={TcDirectoryListingClasses.Paragraph}>
          More information about transplants can be found:
        </p>
        <ul className={classnames('list-disc', 'list-inside')}>
          <li>
            {fields?.listing.txDataByCenterLink?.value?.href && (
              <LinkA11yWrapper
                className={classnames('text-blue-morning', 'underline')}
                field={fields?.listing.txDataByCenterLink}
              />
            )}{' '}
            report at blood cell.transplant.hrsa.gov
          </li>
          <li>
            {fields?.listing.diseaseListLink?.value?.href && (
              <LinkA11yWrapper
                className={classnames('text-blue-morning', 'underline')}
                field={fields?.listing.diseaseListLink}
              />
            )}
          </li>
        </ul>
      </>
    </Container>
  );
};

export default TransplantByDiseaseTab;
